// Client for each box's own read-only /volumes endpoint (HOME-209) — the
// live LVM/mdadm storage layout, replacing the hand-maintained
// drives/{leia,luke}-raid.html static docs. Same fetch plumbing as
// statusApi.ts: fetched directly from the browser on the home LAN, through
// the same mTLS bridge (HOME-184), at
// https://api.<box>.home.nakomis.com/volumes.
//
// A box that's down (or has no `/volumes` data, e.g. mid-deploy) simply
// fails to answer or 503s; we surface that as `reachable: false` rather than
// guessing, same as statusApi's contract.

export interface RawDisk {
    name: string;
    size_bytes: number;
    model?: string;
    serial?: string;
    transport?: string;
    rotational?: boolean;
}

export interface RawArray {
    device: string;
    level: string;
    state: string;
    members: string[];
    devices: string;      // e.g. "4/4"
    slot_state: string;   // e.g. "UUUU"
    resync_action?: string;
    resync_percent?: number;
}

export interface RawVolumeGroup {
    name: string;
    size_bytes: number;
    free_bytes: number;
    pv_count: number;
    lv_count: number;
    attr: string;
}

export interface RawLogicalVolume {
    name: string;
    vg: string;
    size_bytes: number;
    path: string;
    active: boolean;
    attr: string;
}

export interface RawMount {
    source?: string;
    target: string;
    fstype?: string;
    size_bytes?: number;
    used_bytes?: number;
    avail_bytes?: number;
    use_percent?: string;
}

export interface RawVolumes {
    host?: string;
    reported_at?: string;
    disks: RawDisk[];
    arrays: RawArray[];
    volume_groups: RawVolumeGroup[];
    logical_volumes: RawLogicalVolume[];
    mounts: RawMount[];
}

export interface VolumesStatus {
    reachable: boolean;
    reportedAt?: string;
    disks: RawDisk[];
    arrays: RawArray[];
    volumeGroups: RawVolumeGroup[];
    logicalVolumes: RawLogicalVolume[];
    mounts: RawMount[];
    /** Populated when the box couldn't be reached / parsed. */
    error?: string;
}

const TIMEOUT_MS = 4000;

const unreachable = (error: string): VolumesStatus => ({
    reachable: false, disks: [], arrays: [], volumeGroups: [], logicalVolumes: [], mounts: [], error,
});

/** Fetch one box's live volume layout. Never throws — errors become `reachable: false`. */
export async function fetchBoxVolumes(url: string): Promise<VolumesStatus> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
        const res = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!res.ok) return unreachable(`${res.status} ${res.statusText}`);
        const data = (await res.json()) as RawVolumes;
        return {
            reachable: true,
            reportedAt: data.reported_at,
            disks: data.disks ?? [],
            arrays: data.arrays ?? [],
            volumeGroups: data.volume_groups ?? [],
            logicalVolumes: data.logical_volumes ?? [],
            mounts: data.mounts ?? [],
        };
    } catch (e) {
        const msg = e instanceof DOMException && e.name === 'AbortError'
            ? 'timed out'
            : e instanceof Error ? e.message : String(e);
        return unreachable(msg);
    } finally {
        clearTimeout(timer);
    }
}

/** Human byte formatting for the drive-layout card — GiB/TiB, one decimal. */
export function formatBytes(bytes: number | undefined): string {
    if (bytes === undefined || Number.isNaN(bytes)) return '—';
    const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB'];
    let value = bytes;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
        value /= 1024;
        unit += 1;
    }
    return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

/** An mdadm array is degraded if any per-slot marker is down ('_'). */
export function isDegraded(array: RawArray): boolean {
    return array.slot_state.includes('_');
}

/** Used-of-total as a rounded 0-100 percentage, or undefined if either figure is missing. */
export function usagePercent(used?: number, size?: number): number | undefined {
    if (used === undefined || size === undefined || size === 0) return undefined;
    return Math.min(100, Math.round((used / size) * 100));
}

/** Amber/red thresholds (percent used) driving the fullness colour — see config/infra.ts. */
export interface FullnessThresholds {
    amber: number;
    red: number;
}

/** Green/amber/red colour for a given usage percentage against configurable thresholds. */
export function fullnessColor(pct: number, thresholds: FullnessThresholds): string {
    if (pct >= thresholds.red) return '#e53935';
    if (pct >= thresholds.amber) return '#ffb74d';
    return '#4db6ac';
}
